import { useState } from 'react';
import { Link, useNavigate, useLocation, matchPath } from 'react-router-dom';
import { auth } from '../../firebase';
import { useTheme } from '../../context/ThemeContext';
import { useDashboardChrome } from '../../hooks/useDashboardChrome';
import { botIcon, botLabel, companyName } from '../../utils/botHelpers';

export default function Sidebar() {
  const { isDark, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { bots, userData, hasOverdueInvoice, currentUser, serverVersion, openSettings } = useDashboardChrome();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const match = matchPath('/dashboard/bots/:botId/*', location.pathname);
  const currentBotId = match?.params.botId || null;

  const handleLogout = () => {
    const { signOut } = require('firebase/auth');
    signOut(auth).then(() => window.location.reload());
  };

  return (<div style={{
    width: sidebarCollapsed ? '72px' : '240px',
    background: 'var(--bg-sidebar)',
    display: 'flex', flexDirection: 'column',
    flexShrink: 0, position: 'relative',
    transition: 'width 0.3s cubic-bezier(.4,0,.2,1)',
    minHeight: 'calc(100vh - 64px)',
  }}>
    <button
      onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
      style={{
        position: 'absolute', right: '-13px', top: '22px',
        width: '26px', height: '26px', background: '#5B4FE8',
        border: 'none', borderRadius: '50%', color: 'white',
        cursor: 'pointer', zIndex: 10, fontSize: '11px',
      }}
    >
      {sidebarCollapsed ? '→' : '←'}
    </button>

    <div style={{ padding: sidebarCollapsed ? '20px 0' : '20px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
      {!sidebarCollapsed ? (
        <>
          <img src={isDark ? '/Logo-white.png' : '/Logo-black.png'} alt={companyName} style={{ height: '68px', display: 'block', maxWidth: '130px', objectFit: 'contain' }} />
          <div style={{ color: 'var(--text-faint)', fontSize: '10px', marginTop: '4px' }}>Bot Platform</div>
        </>
      ) : (
        <div style={{ textAlign: 'center' }}>
          <img src={isDark ? '/Logo-white.png' : '/Logo-black.png'} alt={companyName} style={{ height: '68px', objectFit: 'contain' }} />
        </div>
      )}
    </div>

    <nav style={{ flex: 1, padding: sidebarCollapsed ? '12px 0' : '12px 8px', overflowY: 'auto' }}>
      {!sidebarCollapsed && (
        <div style={{ fontSize: '9px', color: 'var(--nav-label)', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '8px 8px 4px' }}>Menu</div>
      )}
      {/* Home — returns to landing dashboard */}
      <Link to="/dashboard" style={{ textDecoration: 'none', color: 'inherit' }}>
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: sidebarCollapsed ? '10px 0' : '8px 10px',
            borderRadius: '8px', cursor: 'pointer', marginBottom: '2px',
            background: !currentBotId ? 'rgba(91,79,232,0.2)' : 'transparent',
            color: !currentBotId ? 'var(--nav-item-active)' : 'var(--nav-item)',
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
            transition: 'all 0.1s',
          }}
        >
          <span style={{ fontSize: '15px' }}>🏠</span>
          {!sidebarCollapsed && <span style={{ fontSize: '13px' }}>Dashboard</span>}
        </div>
      </Link>

      {!sidebarCollapsed && (
        <div style={{ fontSize: '9px', color: 'var(--nav-label)', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '16px 8px 4px' }}>My Bots</div>
      )}
      {sidebarCollapsed && <div style={{ height: '12px' }} />}

      {/* ── Account warning banner ── */}
      {!sidebarCollapsed && (() => {
        const payStatus = userData?.paymentStatus;
        const accountLapsed = payStatus === 'expired' || payStatus === 'failed' || payStatus === 'cancelled';
        const showBanner = accountLapsed || hasOverdueInvoice;
        if (!showBanner) return null;
        return (
          <Link to="/billing" style={{ textDecoration: 'none', display: 'block', margin: '0 0 8px 0' }}>
            <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: '8px', padding: '8px 10px', cursor: 'pointer' }}>
              <div style={{ color: '#f87171', fontSize: '11px', fontWeight: '700', marginBottom: '2px' }}>
                ⚠️ {accountLapsed ? 'Subscription Lapsed' : 'Overdue Invoice'}
              </div>
              <div style={{ color: 'rgba(248,113,113,0.7)', fontSize: '10px' }}>
                WhatsApp bots are locked. Tap to go to Billing.
              </div>
            </div>
          </Link>
        );
      })()}

      {bots.length === 0 && !sidebarCollapsed && (
        <div style={{ fontSize: '12px', color: 'var(--nav-label)', padding: '8px 10px' }}>No bots yet</div>
      )}

      {bots.map(bot => {
        const isSuspended = (bot as any).status === 'suspended';
        const payStatus = userData?.paymentStatus;
        const accountLapsed = payStatus === 'expired' || payStatus === 'failed' || payStatus === 'cancelled';
        const isWhatsApp = bot.botType === 'whatsapp' || (!bot.botType && !!(bot as any).phoneNumberId);
        const isLocked = isSuspended || (isWhatsApp && (accountLapsed || hasOverdueInvoice));

        return (
        <div key={bot.id}
          onClick={() => {
            if (isLocked) { navigate('/billing'); return; }
            navigate(`/dashboard/bots/${bot.id}/overview`);
          }}
          title={isLocked ? 'Bot locked — resolve billing to reactivate' : bot.businessName}
          style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: sidebarCollapsed ? '10px 0' : '8px 10px',
            borderRadius: '8px', cursor: isLocked ? 'not-allowed' : 'pointer', marginBottom: '2px',
            background: isLocked
              ? 'rgba(239,68,68,0.06)'
              : currentBotId === bot.id ? '#5B4FE8' : 'transparent',
            color: isLocked
              ? 'rgba(248,113,113,0.5)'
              : currentBotId === bot.id ? 'var(--nav-item-active)' : 'var(--nav-item)',
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
            transition: 'all 0.1s',
            opacity: isLocked ? 0.7 : 1,
          }}
        >
          <span style={{ fontSize: '15px', flexShrink: 0, filter: isLocked ? 'grayscale(1)' : 'none' }}>{botIcon(bot)}</span>
          {!sidebarCollapsed && (
            <>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bot.businessName}</div>
                <div style={{ fontSize: '10px', opacity: 0.6, marginTop: '1px' }}>{botLabel(bot)}</div>
              </div>
              {isLocked && (
                <span style={{ marginLeft: 'auto', background: 'rgba(220,38,38,0.8)', color: 'white', fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '20px', flexShrink: 0 }}>🔒</span>
              )}
            </>
          )}
        </div>
        );
      })}

      <Link to="/create" style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: sidebarCollapsed ? '10px 0' : '8px 10px',
        borderRadius: '8px', color: 'var(--nav-subtle)',
        textDecoration: 'none', marginTop: '4px',
        justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
      }}>
        <span style={{ fontSize: '15px' }}>＋</span>
        {!sidebarCollapsed && <span style={{ fontSize: '12px' }}>New bot</span>}
      </Link>

      {!sidebarCollapsed && (
        <div style={{ fontSize: '9px', color: 'var(--nav-label)', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '16px 8px 4px' }}>Account</div>
      )}
      {sidebarCollapsed && <div style={{ height: '12px' }} />}

      {[
        { to: '/billing', icon: '💳', label: 'Billing' },
        { to: '/datasets', icon: '📦', label: 'Datasets' },
        { to: '/media', icon: '🖼️', label: 'Media' },
        { to: '/database', icon: '🗄️', label: 'Database' },
        { to: '/documentation', icon: '📚', label: 'Docs' },
        { to: '/support', icon: '🆘', label: 'Support' },
      ].map(item => (
        <Link key={item.to} to={item.to} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: sidebarCollapsed ? '10px 0' : '8px 10px',
            borderRadius: '8px', cursor: 'pointer', marginBottom: '2px',
            color: 'var(--nav-item)',
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
          }}>
            <span style={{ fontSize: '15px' }}>{item.icon}</span>
            {!sidebarCollapsed && <span style={{ fontSize: '13px' }}>{item.label}</span>}
          </div>
        </Link>
      ))}

      <div onClick={() => {
        if (!currentBotId) { alert('Select a bot first'); return; }
        openSettings(currentBotId);
      }} style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: sidebarCollapsed ? '10px 0' : '8px 10px',
        borderRadius: '8px', cursor: 'pointer', marginBottom: '2px',
        color: 'var(--nav-item)',
        justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
      }}>
        <span style={{ fontSize: '15px' }}>⚙️</span>
        {!sidebarCollapsed && <span style={{ fontSize: '13px' }}>Settings</span>}
      </div>

      <Link to="/account" style={{ textDecoration: 'none', color: 'inherit' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: sidebarCollapsed ? '10px 0' : '8px 10px',
          borderRadius: '8px', cursor: 'pointer', marginBottom: '2px',
          color: 'var(--nav-item)',
          justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
        }}>
          <span style={{ fontSize: '15px' }}>👤</span>
          {!sidebarCollapsed && <span style={{ fontSize: '13px' }}>Profile</span>}
        </div>
      </Link>

      {/* Theme toggle */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarCollapsed ? 'center' : 'space-between',
          padding: sidebarCollapsed ? '10px 0' : '8px 14px',
          margin: '4px 0',
        }}
      >
        {!sidebarCollapsed && (
          <span style={{ fontSize: '12px', color: 'var(--text-faint)', userSelect: 'none' }}>
            {isDark ? '🌙 Dark' : '☀️ Light'}
          </span>
        )}
        <div
          onClick={toggle}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{
            width: '44px', height: '24px', borderRadius: '12px',
            background: isDark ? 'var(--bg-element)' : '#5B4FE8',
            border: `1px solid ${isDark ? 'var(--border)' : '#4338ca'}`,
            position: 'relative', cursor: 'pointer',
            transition: 'background 0.25s, border-color 0.25s',
            flexShrink: 0,
          }}
        >
          <div style={{
            position: 'absolute', top: '3px',
            left: isDark ? '3px' : '21px',
            width: '16px', height: '16px', borderRadius: '50%',
            background: isDark ? 'var(--text-faint)' : 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '9px', lineHeight: 1,
            transition: 'left 0.25s, background 0.25s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          }}>
            {isDark ? '🌙' : '☀️'}
          </div>
        </div>
      </div>

      <div onClick={handleLogout} style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: sidebarCollapsed ? '10px 0' : '8px 10px',
        borderRadius: '8px', cursor: 'pointer',
        color: 'var(--nav-subtle)',
        justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
      }}>
        <span style={{ fontSize: '15px' }}>🚪</span>
        {!sidebarCollapsed && <span style={{ fontSize: '13px' }}>Logout</span>}
      </div>
    </nav>

    {!sidebarCollapsed && currentUser && (
      <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border-subtle)' }}>
        <div style={{ fontSize: '11px', color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentUser.email}</div>
        {userData?.plan && (() => {
          const payStatus = userData?.paymentStatus;
          const accountLapsed = payStatus === 'expired' || payStatus === 'failed' || payStatus === 'cancelled';
          const hasIssue = accountLapsed || hasOverdueInvoice;
          return (
            <div style={{ display: 'inline-block', marginTop: '4px', background: hasIssue ? 'rgba(239,68,68,0.15)' : '#5B4FE822', color: hasIssue ? '#f87171' : '#818cf8', fontSize: '10px', padding: '2px 8px', borderRadius: '10px', fontWeight: '600', textTransform: 'capitalize' }}>
              {hasIssue ? '⚠️ ' : ''}{userData.plan} plan{hasIssue ? ' — lapsed' : ''}
            </div>
          );
        })()}
        {serverVersion && (
          <div style={{ fontSize: '10px', color: 'var(--text-faint)', marginTop: '6px' }}>
            server <span style={{ fontWeight: '600', color: 'var(--text-muted)' }}>v{serverVersion}</span>
          </div>
        )}
      </div>
    )}
  </div>);
}
